import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import PageHeader from "./-components/PageHeader";
import ConsoleHrozontalHeadTable from "./-components/ConsolehorizontalHeadTable";
import { usePutConsoleSupplies } from "hooks/console/usePutConsoleSupplies";
import { useGetConsoleSuppliesDetail } from "hooks/console/useGetConsoleSuppliesDetail";
import LoadingPop from "components/popup/LoadingPop";
import { useDeleteConsoleSupplies } from "hooks/console/useDeleteConsoleSupplies";
import { RouteTypes, toastErrorSyle } from "hooks/utils/util";
import SuppliesForm, { SuppliesFormProps } from "./common/SuppliesForm";
import Comment from "./-components/Comment";
import { useDeleteConsoleSuppliesComment } from "hooks/console/useDeleteConsoleSuppliesComment";
import { usePutConsoleSuppliesComment } from "hooks/console/usePutConsoleSuppliesComment";
import { usePostConsoleSuppliesComment } from "hooks/console/usePostConsoleSuppliesComment";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import ConfirmDialog from "components/popup/ConfirmDialog";
import Seo from "components/Seo";

const initialForm = {
    status: "",
    name: "",
    category: "",
    list_no: "",
    subject: "",
    contents: "",
    company: "",
    mobile: "",
    address: "",
    w_date: "",
    comment: [],
};

export default function ConsoleSuppliesDetail({ type }: { type: RouteTypes }) {
    const navigate = useNavigate();
    const params = useParams();
    const idx = params.idx;
    const [editMode, setEditMode] = useState(false);
    const [form, setForm] = useState<SuppliesFormProps>(initialForm);
    const { data, isLoading, refetch: refetchDetail } = useGetConsoleSuppliesDetail({ category: type, list_no: idx ?? "" });
    const { mutate: updateSupplies } = usePutConsoleSupplies();
    const { mutate: deleteSupplies } = useDeleteConsoleSupplies();

    const { mutate: postComment } = usePostConsoleSuppliesComment();
    const { mutate: updateComment } = usePutConsoleSuppliesComment();
    const { mutate: deleteComment } = useDeleteConsoleSuppliesComment();

    useEffect(() => {
        if (data) {
            setForm(() => ({
                category: type,
                status: data.status,
                name: data.name,
                list_no: data.list_no.toString(),
                subject: data.subject,
                contents: data.contents,
                company: data.company,
                mobile: data.mobile,
                address: data.address,
                w_date: data.w_date,
                comment: data.comment,
                // notice: data.notice,
                // b_file: data.b_file,
            }));
        }
    }, [data]);

    const thead = [
        {
            title: "처리상태",
            key: "status",
            content: (value: string) => (
                <Select value={value} onValueChange={value => handleChangeRowStatus(value)}>
                    <SelectTrigger
                        className={`relative text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2  border-none shadow-none max-w-[100px] justify-center ${value === "Y" ? "text-[#4F46E5]" : "text-[#999999]"}`}
                    >
                        <SelectValue placeholder="" />
                        <ArrowDown className="[&_path]:fill-[#1C1B1F]" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                        <SelectItem value="Y">처리완료</SelectItem>
                        <SelectItem value="N">접수중</SelectItem>
                    </SelectContent>
                </Select>
            ),
        },
        { title: "제목", key: "subject", tdClassName: "px-5" },
        { title: "회사명", key: "company", tdClassName: "px-5" },
        { title: "작성자", key: "name", tdClassName: "px-5" },
        { title: "작성일", key: "w_date", tdClassName: "px-5" },
        { title: "전화번호", key: "mobile", tdClassName: "px-5" },
        { title: "주소", key: "address", tdClassName: "px-5" },
        {
            title: "내용",
            key: "contents",
            content: (value: string) => <div className="py-3 px-3" dangerouslySetInnerHTML={{ __html: value }} />,
        },
    ];

    function handleChangeRowStatus(value: string) {
        if (data) {
            updateSupplies(
                {
                    ...form,
                    status: value,
                },
                {
                    onSuccess: () => {
                        toast.success("성공적으로 수정 되었습니다.");
                        refetchDetail();
                    },
                    onError: (error: any) => {
                        const message = error.message;
                        toast.error(message, toastErrorSyle);
                    },
                },
            );
        }
    }

    const handleDeleteSupplies = () => {
        if (data) {
            deleteSupplies(
                { category: type, list_no: [data.list_no] },
                {
                    onSuccess: () => {
                        toast.success("성공적으로 삭제 되었습니다.");
                        navigate(-1);
                    },
                    onError: (error: any) => {
                        const message = error.message;
                        toast.error(message, toastErrorSyle);
                    },
                },
            );
        }
    };

    // 코멘트 등록
    const handlePostComment = (value: string, successCallback: () => void) => {
        if (idx) {
            postComment(
                {
                    category: type,
                    list_no: idx,
                    c_content: value,
                },
                {
                    onSuccess: () => {
                        toast.success("성공적으로 등록 되었습니다.");
                        refetchDetail();
                        successCallback();
                    },
                    onError: (error: any) => {
                        const message = error.message;
                        toast.error(message, toastErrorSyle);
                    },
                },
            );
        }
    };
    // 코멘트 수정
    const handleUpdateComment = (cListNo: string, content: string) => {
        if (idx) {
            updateComment(
                {
                    category: type,
                    list_no: idx,
                    c_list_no: cListNo,
                    c_content: content,
                },
                {
                    onSuccess: () => {
                        toast.success("성공적으로 수정 되었습니다.");
                        refetchDetail();
                    },
                    onError: (error: any) => {
                        const message = error.message;
                        toast.error(message, toastErrorSyle);
                    },
                },
            );
        }
    };
    // 코멘트 삭제
    const handleDeleteComment = (cListNo: string) => {
        if (idx) {
            deleteComment(
                {
                    category: type,
                    list_no: idx,
                    c_list_no: cListNo,
                },
                {
                    onSuccess: () => {
                        toast.success("성공적으로 삭제 되었습니다.");
                        refetchDetail();
                    },
                    onError: (error: any) => {
                        const message = error.message;
                        toast.error(message, toastErrorSyle);
                    },
                },
            );
        }
    };

    return (
        <>
            <Seo type="console" pageTitle="운송장 및 부자재 요청" />
            <div className="flex-1 flex flex-col overflow-hidden max-h-[100dvh]">
                <PageHeader category="고객서비스" title="운송장 및 부자재 요청" />
                <div className="flex-1 bg-[#F7F6FB] flex overflow-hidden">
                    <div className=" px-[40px] py-[28px] w-full overflow-auto">
                        <div className="bg-white rounded-[20px] py-[36px] px-[40px] w-full min-w-[800px]">
                            {isLoading ? (
                                <LoadingPop />
                            ) : (
                                <div>
                                    {data && (
                                        <>
                                            {editMode ? (
                                                <SuppliesForm
                                                    type={type}
                                                    form={form}
                                                    setForm={setForm}
                                                    setEditMode={setEditMode}
                                                    update
                                                    refetchDetail={refetchDetail}
                                                />
                                            ) : (
                                                <>
                                                    <ConsoleHrozontalHeadTable thead={thead} tbody={data} />
                                                    <div className="flex justify-center gap-x-[8px] mt-5">
                                                        <button
                                                            onClick={() => setEditMode(true)}
                                                            className="text-[#666666] text-[18px] bg-white rounded-[5px] border border-[#dddddd] px-[55px] py-[15px]"
                                                        >
                                                            수정
                                                        </button>
                                                        <ConfirmDialog
                                                            title="요청 삭제"
                                                            trigger={open => (
                                                                <button
                                                                    type="button"
                                                                    onClick={open}
                                                                    className="text-[#666666] text-[18px] bg-white rounded-[5px] border border-[#dddddd] px-[55px] py-[15px]"
                                                                >
                                                                    삭제
                                                                </button>
                                                            )}
                                                            handleConfirm={handleDeleteSupplies}
                                                        />
                                                        <button
                                                            onClick={() => navigate(-1)}
                                                            className="text-white text-[18px] bg-[#4F46E5] rounded-[5px] border border-[#666666] px-[55px] py-[15px]"
                                                        >
                                                            목록
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}

                                    <Comment
                                        commentData={form.comment}
                                        postComment={handlePostComment}
                                        updateComment={handleUpdateComment}
                                        deleteComment={handleDeleteComment}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
